// src/hooks/useResizableSidebar.ts
import { useEffect, useRef } from 'react';

interface ResizableOptions {
  minWidth: number;
  maxWidth: number;
  defaultWidth: number;
  onResize?: (width: number) => void;
}

export function useResizableSidebar(options: ResizableOptions) {
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    
    let isResizing = false;
    let lastDownX = 0;
    let originalWidth = 0;
    
    const handleMouseDown = (e: MouseEvent) => {
      const currentWidth = sidebar.offsetWidth;
      
      // Only allow resizing from the right edge (approximately 10px from the edge)
      if (e.offsetX > currentWidth - 10) {
        isResizing = true;
        lastDownX = e.clientX;
        originalWidth = currentWidth;
        
        // Add a class to show we're resizing
        sidebar.classList.add('resizing');
        
        // Prevent text selection during resize
        document.body.style.userSelect = 'none';
      }
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      // Calculate new width and respect min/max constraints
      let newWidth = originalWidth + (e.clientX - lastDownX);
      newWidth = Math.max(options.minWidth, Math.min(options.maxWidth, newWidth));
      
      sidebar.style.width = `${newWidth}px`;
      
      // Call the onResize callback if provided
      if (options.onResize) {
        options.onResize(newWidth);
      }
    };
    
    const handleMouseUp = () => {
      if (isResizing) {
        isResizing = false;
        sidebar.classList.remove('resizing');
        document.body.style.userSelect = '';
      }
    };
    
    // Set initial width if provided
    if (options.defaultWidth) {
      sidebar.style.width = `${options.defaultWidth}px`;
    }
    
    // Add event listeners
    sidebar.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Cleanup event listeners on unmount
    return () => {
      sidebar.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [options]);
  
  return sidebarRef;
}